/**
 * Formatting helpers + components.
 *
 * All numeric display uses tabular numerals (CSS) and these helpers, so
 * alignment is consistent across the app and decimals never wiggle.
 */

import type { ReactNode } from 'react';
import { Decimal } from '../engine/money.js';

const usdNoDec = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0, minimumFractionDigits: 0 });
const usdTwoDec = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const pctTwoDec = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2, minimumFractionDigits: 2 });

export function formatUsd(v: Decimal | null | undefined, opts: { decimals?: 0 | 2 } = {}): string {
  if (v == null) return '-';
  const num = v.toNumber();
  return (opts.decimals === 0 ? usdNoDec : usdTwoDec).format(num);
}

export function formatBtc(v: Decimal | null | undefined, decimals = 8): string {
  if (v == null) return '-';
  return v.toFixed(decimals);
}

export function formatSats(v: bigint): string {
  return v.toLocaleString('en-US');
}

export function formatPct(v: Decimal | null | undefined): string {
  if (v == null) return '-';
  return pctTwoDec.format(v.toNumber());
}

export function formatDate(d: Date | null | undefined): string {
  if (!d) return '-';
  return d.toISOString().slice(0, 10);
}

// ---------- components ----------

export function Money({ usd, decimals = 2 }: { usd: Decimal; decimals?: 0 | 2 }): ReactNode {
  return <span className="mono">${formatUsd(usd, { decimals })}</span>;
}

export function Pct({ value }: { value: Decimal }): ReactNode {
  const sign = value.greaterThan(0) ? 'pos' : value.lessThan(0) ? 'neg' : 'zero';
  return <span className={`mono delta ${sign}`}>{value.greaterThan(0) ? '+' : ''}{formatPct(value)}</span>;
}

export function Delta({ usd }: { usd: Decimal }): ReactNode {
  const sign = usd.greaterThan(0) ? 'pos' : usd.lessThan(0) ? 'neg' : 'zero';
  const prefix = usd.greaterThan(0) ? '+$' : usd.lessThan(0) ? '−$' : '$';
  return (
    <span className={`mono delta ${sign}`}>
      {prefix}{formatUsd(usd.abs(), { decimals: 2 })}
    </span>
  );
}
